import Layout from "@/components/Layout";
import Ornament from "@/components/Ornament";
import { EXTERIOR_SKETCH, INTERIOR_MOOD, CARNIVAL, LOGO_URL } from "@/lib/constants";

const TIMELINE = [
  {
    year: "1882",
    title: "Le radici",
    text:
      "Lo stemma di famiglia, con corona e croce, prende forma. È l'inizio di un legame fra il nome Pedrocchi e la convivialità veneziana — un'eredità di valori che attraverserà generazioni.",
    img: LOGO_URL,
    align: "left",
  },
  {
    year: "1971",
    title: "L'apertura",
    text:
      "Paolo e Renata Puntar aprono il Ristorante-Pizzeria Pedrocchi nel cuore di Venezia. Tavoli all'aperto, cucina veneziana di stagione e il profumo della pizza cotta a legna riempiono la calle.",
    img: EXTERIOR_SKETCH,
    align: "right",
  },
  {
    year: "1990",
    title: "I sapori del mondo",
    text:
      "La carta si apre alle cucine internazionali. Senza tradire la tradizione veneziana, il locale diventa punto d'incontro per viaggiatori di ogni cultura.",
    img: INTERIOR_MOOD,
    align: "left",
  },
  {
    year: "2023",
    title: "La nuova generazione",
    text:
      "I figli Manuel e Cristiana raccolgono il testimone. Conservano l'anima della casa e la portano avanti con freschezza, attenzione e una nuova sensibilità contemporanea.",
    img: CARNIVAL,
    align: "right",
  },
  {
    year: "2026",
    title: "Locale Storico",
    text:
      "Il Ristorante Pedrocchi riceve l'onorificenza ufficiale di Locale Storico — un riconoscimento alla continuità, alla passione e alla famiglia Puntar.",
    img: null,
    align: "left",
  },
];

export default function StoriaPage() {
  return (
    <Layout>
      <section className="border-b border-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-12 md:pb-20">
          <div className="overline mb-6">Capitolo I — Storia</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-4xl">
            Una famiglia,<br />
            <em className="text-wine">una cucina,</em><br />
            due generazioni.
          </h1>
          <p className="text-ink-mid mt-8 max-w-2xl leading-relaxed text-base md:text-lg">
            La nostra storia parte da lontano. È fatta di mani, di volti, di
            ricette che si tramandano e di Venezia che resta sullo sfondo come
            una scenografia naturale.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="relative">
            {/* Central vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-ink/40" />

            <div className="flex flex-col gap-24 md:gap-32">
              {TIMELINE.map((step, i) => (
                <div
                  key={step.year}
                  className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center relative"
                  data-testid={`timeline-step-${step.year}`}
                >
                  {/* Year side */}
                  <div className={step.align === "left" ? "md:order-1" : "md:order-2"}>
                    <div className="overline mb-4">{step.title}</div>
                    <div className="font-serif text-7xl md:text-8xl xl:text-9xl text-ink leading-none tracking-tighter">
                      {step.year}
                    </div>
                    <p className="text-ink-mid mt-6 max-w-md leading-relaxed">
                      {step.text}
                    </p>
                  </div>

                  {/* Image side */}
                  <div className={step.align === "left" ? "md:order-2" : "md:order-1"}>
                    {step.img ? (
                      <div className="aspect-[4/5] border border-ink overflow-hidden">
                        <img src={step.img} alt={step.title} className="w-full h-full object-cover duotone" />
                      </div>
                    ) : (
                      <div className="aspect-[4/5] border border-ink flex items-center justify-center bg-paper-dark">
                        <div className="seal" style={{ transform: "scale(1.3)" }}>
                          <span className="seal-cap">Locale</span>
                          <span className="seal-year">2026</span>
                          <span className="seal-cap">Storico</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dot on the line */}
                  <span className="hidden md:block absolute left-1/2 -translate-x-1/2 top-12 w-3 h-3 bg-wine border border-ink rounded-full z-10" />
                </div>
              ))}
            </div>
          </div>

          <Ornament />

          <div className="max-w-3xl mx-auto text-center">
            <div className="overline mb-6">Oggi</div>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed italic">
              "Crediamo che servire bene un cliente sia il modo più semplice
              per onorare chi ci ha preceduti."
            </p>
            <div className="overline mt-6">— Manuel &amp; Cristiana Puntar</div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
